import React from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { panelByIdSelector, haveMultiPanelSelector } from 'store';
import { Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ProgressBar } from '@app/core';
import { CsvImport } from '@app/api';

const useStyles = createUseStyles((theme: Theme) => ({
  banner: {
    backgroundColor: theme.colors.blueMuted,
    minHeight: theme.height.small,
    borderRadius: theme.borderRadius.std,

    paddingLeft: theme.marginBase * 2,
    marginBottom: theme.marginBase,

    overflow: 'hidden',
    flexWrap: 'wrap',

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  bannerSuccess: {
    backgroundColor: theme.colors.greenMuted,
  },
  bannerFailure: {
    backgroundColor: theme.colors.redMuted,
  },
  text: {
    ...theme.fonts.caption,
    marginRight: theme.marginBase * 2,
  },
  progressContainer: {
    flex: 1,
    minWidth: theme.marginBase * 24,
    maxWidth: theme.marginBase * 48,
    marginRight: theme.marginBase * 2,

    height: theme.height.tiny,
    display: 'flex',
    alignItems: 'center',
  },
  progressBar: {
    zIndex: 1,
    width: '100%',

    marginTop: 0,
  },
  importResults: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.marginBase * 2,
  },
  importResultsSuccess: {
    ...theme.fonts.label,
  },
  importResultsError: {
    ...theme.fonts.label,
    color: theme.colors.redAccessible,
    display: 'flex',
    alignItems: 'center',
  },
  separator: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: theme.colors.inkPrimary,
    margin: theme.marginBase,
  },
  downloadIcon: {
    fontSize: theme.iconSize.small,
    color: theme.colors.redAccessible,
    marginLeft: theme.marginBase / 2,
  },
}));

interface Props {
  csvImport: CsvImport;
}

export const CsvImportBanner = ({ csvImport }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const panel = useSelector((state: any) =>
    panelByIdSelector(state, csvImport.panelId),
  );
  const haveMultiPanel = useSelector(haveMultiPanelSelector);

  if (!csvImport.filename || !panel) {
    return null;
  }

  if (csvImport.importFinishedAt) {
    if (csvImport.status === 'SUCCESS') {
      return (
        <div className={classnames(classes.banner, classes.bannerSuccess)}>
          <p className={classes.text}>
            {haveMultiPanel ? (
              <Trans
                i18nKey="participants.list.importCsv.progress.success"
                values={{ filename: csvImport.filename, panelName: panel.name }}
              />
            ) : (
              <Trans
                i18nKey="participants.list.importCsv.progress.successOnePanel"
                values={{ filename: csvImport.filename }}
              />
            )}
          </p>
          <div className={classes.importResults}>
            <div className={classes.importResultsSuccess}>
              <Trans
                i18nKey="participants.list.importCsv.progress.added"
                values={{ count: csvImport.addedCount }}
              />
            </div>
            <div className={classes.separator} />
            <div className={classes.importResultsSuccess}>
              <Trans
                i18nKey="participants.list.importCsv.progress.updated"
                values={{ count: csvImport.updatedCount }}
              />
            </div>
            <div className={classes.separator} />
            <div className={classes.importResultsSuccess}>
              <Trans
                i18nKey="participants.list.importCsv.progress.ignored"
                values={{ count: csvImport.ignoredCount }}
              />
            </div>
            <div className={classes.separator} />
            {!!csvImport.errorFileUrl ? (
              <a
                href={csvImport.errorFileUrl || '#'}
                download
                target="_blank"
                rel="noopener noreferrer"
                title="Download errors"
                className={classes.importResultsError}
              >
                <div>
                  <Trans
                    i18nKey="participants.list.importCsv.progress.error"
                    values={{ count: csvImport.errorCount }}
                  />
                </div>
                <i
                  className={classnames(
                    classes.downloadIcon,
                    'ri-download-line',
                  )}
                />
              </a>
            ) : (
              <div className={classes.importResultsError}>
                <Trans
                  i18nKey="participants.list.importCsv.progress.error"
                  values={{ count: csvImport.errorCount }}
                />
              </div>
            )}
          </div>
        </div>
      );
    } else {
      return (
        <div className={classnames(classes.banner, classes.bannerFailure)}>
          <p className={classes.text}>
            {haveMultiPanel ? (
              <Trans
                i18nKey="participants.list.importCsv.progress.failure"
                values={{ filename: csvImport.filename, panelName: panel.name }}
              />
            ) : (
              <Trans
                i18nKey="participants.list.importCsv.progress.failureOnePanel"
                values={{ filename: csvImport.filename }}
              />
            )}
          </p>
        </div>
      );
    }
  } else {
    return (
      <div className={classes.banner}>
        <p className={classes.text}>
          {haveMultiPanel ? (
            <Trans
              i18nKey="participants.list.importCsv.progress.inProgress"
              values={{ filename: csvImport.filename, panelName: panel.name }}
            />
          ) : (
            <Trans
              i18nKey="participants.list.importCsv.progress.inProgressOnePanel"
              values={{ filename: csvImport.filename }}
            />
          )}
        </p>
        <div className={classes.progressContainer}>
          <ProgressBar
            progression={csvImport.progress}
            fullSize={100}
            barHeight={2}
            className={classes.progressBar}
          />
        </div>
      </div>
    );
  }
};
